import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Upload, X, Search, Package, CheckCircle2, Sliders, Check, UserCircle, CreditCard, ShoppingBag, PackagePlus, Monitor, Truck, ShieldAlert, Wrench, AlertOctagon, MessageSquare, HelpCircle } from 'lucide-react';
import { ticketAPI, productAPI } from '../../api';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';

const CATEGORIES = {
  "Account Issue": ["Account Registration", "Login Issue", "Forgot Password", "Profile Update", "Account Verification"],
  "Payment Issue": ["Payment Failed", "Payment Refund", "Billing Issue", "Invoice Request"],
  "Order Issue": ["Order Status", "Wrong Order", "Order Cancellation"],
  "Product Issue": ["Product Information", "Product Not Available", "Product Quality Issue", "Wrong Product Received", "Product Return / Replacement"],
  "Technical Support": ["Website Not Working", "App Crash", "Slow Performance", "Feature Not Working", "API Integration Issue"],
  "Delivery Issue": ["Delivery Delay", "Tracking Issue", "Wrong Delivery Address", "Missing Package"],
  "Seller Support": ["Seller Registration", "Product Upload Issue", "Inventory Issue", "Commission Query", "Settlement Issue"],
  "Service Request": ["New Service Request", "Service Reschedule", "Service Cancellation", "Service Feedback"],
  "Complaint": ["Complaint"],
  "Feedback": ["Feedback", "Suggestion"],
  "Add Product": ["New Product Request", "Bulk Upload Request", "Other"],
  "Other": ["Enquiry", "Other"]
};


const CATEGORY_META: Record<string, { color: string, bg: string, icon: React.ReactNode }> = {
  "Account Issue": { color: "#3b82f6", bg: "#eff6ff", icon: <UserCircle size={20} /> },
  "Payment Issue": { color: "#8b5cf6", bg: "#f5f3ff", icon: <CreditCard size={20} /> },
  "Order Issue": { color: "#f59e0b", bg: "#fffbeb", icon: <ShoppingBag size={20} /> },
  "Product Issue": { color: "#10b981", bg: "#ecfdf5", icon: <Package size={20} /> },
  "Technical Support": { color: "#6366f1", bg: "#eef2ff", icon: <Monitor size={20} /> },
  "Delivery Issue": { color: "#f97316", bg: "#fff7ed", icon: <Truck size={20} /> },
  "Seller Support": { color: "#ec4899", bg: "#fdf2f8", icon: <ShieldAlert size={20} /> },
  "Service Request": { color: "#14b8a6", bg: "#f0fdfa", icon: <Wrench size={20} /> },
  "Complaint": { color: "#ef4444", bg: "#fef2f2", icon: <AlertOctagon size={20} /> },
  "Feedback": { color: "#84cc16", bg: "#f7fee7", icon: <MessageSquare size={20} /> },
  "Add Product": { color: "#06b6d4", bg: "#ecfeff", icon: <PackagePlus size={20} /> },
  "Other": { color: "#64748b", bg: "#f8fafc", icon: <HelpCircle size={20} /> },
};

export default function CreateTicket() {
  const navigate = useNavigate();
  const location = useLocation();
  const ticketsLeft = location.state?.ticketsLeft || 20;
  const [loading, setLoading] = useState(false);
  const [images, setImages] = useState<File[]>([]);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const categoryRef = useRef<HTMLDivElement>(null);
  const [priorityOpen, setPriorityOpen] = useState(false);
  const priorityRef = useRef<HTMLDivElement>(null);
  const [productCategoryOpen, setProductCategoryOpen] = useState(false);
  const productCategoryRef = useRef<HTMLDivElement>(null);
  const [productStatusOpen, setProductStatusOpen] = useState(false);
  const productStatusRef = useRef<HTMLDivElement>(null);

  const PRIORITY_OPTIONS = [
    { label: 'Low', color: '#10b981' },
    { label: 'Medium', color: '#f59e0b' },
    { label: 'High', color: '#f97316' },
    { label: 'Critical', color: '#ef4444' },
  ];


  const PRODUCT_STATUS_OPTIONS = [
    { label: 'Active', color: '#10b981' },
    { label: 'Inactive', color: '#94a3b8' },
  ];

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (categoryRef.current && !categoryRef.current.contains(e.target as Node)) setCategoryOpen(false);
      if (priorityRef.current && !priorityRef.current.contains(e.target as Node)) setPriorityOpen(false);
      if (productCategoryRef.current && !productCategoryRef.current.contains(e.target as Node)) setProductCategoryOpen(false);
      if (productStatusRef.current && !productStatusRef.current.contains(e.target as Node)) setProductStatusOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const [formData, setFormData] = useState({
    category: location.state?.category || '',
    subCategory: '',
    priority: 'Medium',
    subject: '',
    description: '',
    productName: '',
    subTitle: '',
    productCategory: '',
    productSubCategory: '',
    brandName: '',
    tags: '',
    paymentModes: ['Online'] as string[],
    warranty: '',
    replacementDays: '7',
    productStatus: 'Active',
    weight: '',
    height: '',
    width: '',
    keySpecifications: '',
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const [dbCategories, setDbCategories] = useState<{ category: string }[]>([]);
  // Fetch product categories on mount
  useEffect(() => {
    ticketAPI.getCategories()
      .then((res: any) => {
        if (res.data?.response === 'S') {
          setDbCategories(res.data.data || []);
        }
      })
      .catch((err) => console.error('Error fetching categories:', err));
  }, []);

  // Product card search state
  const [productSearch, setProductSearch] = useState('');
  const [allProducts, setAllProducts] = useState<any[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<any[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [loadingProducts, setLoadingProducts] = useState(false);

  // Load seller products when Product Issue is selected
  useEffect(() => {
    if (location.state?.productRequest === true && allProducts.length === 0) {
      setLoadingProducts(true);
      productAPI.getAll({ page: 1, limit: 100 })
        .then((res: any) => {
          if (res.data.response === 'S') {
            setAllProducts(res.data.data || []);
          }
        })
        .catch(() => { })
        .finally(() => setLoadingProducts(false));
    }
  }, [formData.category]);

  useEffect(() => {
    if (!productSearch.trim()) {
      setFilteredProducts(allProducts.slice(0, 6));
    } else {
      const q = productSearch.toLowerCase();
      setFilteredProducts(
        allProducts.filter((p: any) =>
          (p.name || p.productName || '').toLowerCase().includes(q) ||
          (p.brand || p.brandName || '').toLowerCase().includes(q) ||
          (p.category || p.productCategory || '').toLowerCase().includes(q)
        ).slice(0, 6)
      );
    }
  }, [productSearch, allProducts]);

  const handleSelectProduct = (product: any) => {
    setSelectedProduct(product);
    setFormData(prev => ({
      ...prev,
      productName: product.name || product.productName || '',
      brandName: product.brand || product.brandName || '',
      productCategory: product.category || product.productCategory || '',
      subTitle: product.subTitle || '',
      tags: Array.isArray(product.tags) ? product.tags.join(', ') : (product.tags || ''),
    }));
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const filesArray = Array.from(e.target.files);
      
      const newValidFiles: File[] = [];
      let duplicateFound = false;
      let limitReached = false;

      for (const file of filesArray) {
        if (images.length + newValidFiles.length >= 5) {
          limitReached = true;
          break; // Stop adding if we hit the limit of 5
        }

        const isDuplicate = images.some(img => img.name === file.name && img.size === file.size) || 
                            newValidFiles.some(img => img.name === file.name && img.size === file.size);
        
        if (isDuplicate) {
          duplicateFound = true;
        } else {
          newValidFiles.push(file);
        }
      }

      if (limitReached && duplicateFound) {
        Swal.fire({
          icon: 'warning',
          title: 'Maximum 5 Images Allowed',
          text: 'You have selected more than 5 images. Above 5 are not allowed. Also, some duplicates were ignored.',
          confirmButtonColor: '#f59e0b'
        });
      } else if (limitReached) {
        Swal.fire({
          icon: 'warning',
          title: 'Maximum 5 Images Allowed',
          text: 'You have selected more than 5 images. Above 5 are not allowed.',
          confirmButtonColor: '#f59e0b'
        });
      } else if (duplicateFound) {
        Swal.fire({
          icon: 'warning',
          title: 'Duplicate Image Detected',
          text: 'One or more selected images are duplicates and have been ignored.',
          confirmButtonColor: '#f59e0b'
        });
      }

      if (newValidFiles.length > 0) {
        setImages([...images, ...newValidFiles]);
      }
      
      e.target.value = '';
    }
  };

  const removeImage = (index: number) => {
    const newImages = [...images];
    newImages.splice(index, 1);
    setImages(newImages);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const isProductRequestForm = formData.category === 'Add Product' || location.state?.productRequest === true;

    const newErrors: { [key: string]: string } = {};

    if (!formData.category) {
      newErrors.category = 'Please select a category';
    }
    if (isProductRequestForm) {
      if (!formData.subject) newErrors.subject = 'Subject is required';
      if (!formData.productName) newErrors.productName = 'Product Name is required';
      if (!formData.productCategory) newErrors.productCategory = 'Product Category is required';
      if (!formData.productSubCategory?.trim()) newErrors.productSubCategory = 'Product Sub Category is required';
    } else {
      if (formData.category) {
        if (!formData.subCategory?.trim()) newErrors.subCategory = 'Sub Category is required';
        if (!formData.subject) newErrors.subject = 'Subject is required';
        if (!formData.description) newErrors.description = 'Description is required';
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error('Please fill all mandatory fields');
      return;
    }

    const result = await Swal.fire({
      title: 'Submit Ticket?',
      text: "Are you sure you want to submit this request?",
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#14b8a6',
      cancelButtonColor: '#94a3b8',
      confirmButtonText: 'Yes, submit it!'
    });

    if (!result.isConfirmed) {
      return;
    }

    setLoading(true);
    try {
      const data = new FormData();
      data.append('category', formData.category);
      data.append('priority', formData.priority);

      if (isProductRequestForm) {
        data.append('subject', formData.subject);
        data.append('description', formData.description || '');
        data.append('productName', formData.productName);
        data.append('subTitle', formData.subTitle);
        data.append('productCategory', formData.productCategory);
        data.append('productSubCategory', formData.productSubCategory);
        data.append('subCategory', formData.productSubCategory);
        data.append('brandName', formData.brandName);
        data.append('tags', formData.tags);
        data.append('warranty', formData.warranty);
        data.append('replacementDays', formData.replacementDays);
        data.append('productStatus', formData.productStatus);
        data.append('weight', formData.weight);
        data.append('height', formData.height);
        data.append('width', formData.width);
        data.append('keySpecifications', formData.keySpecifications);
      } else {
        data.append('subCategory', formData.subCategory);
        data.append('subject', formData.subject);
        data.append('description', formData.description);
      }

      images.forEach((img) => {
        data.append('images', img);
      });
      data.append('ticketsLeft', ticketsLeft.toString());

      const res = await ticketAPI.create(data);
      if (res.data.response === 'S') {
        await Swal.fire({
          title: 'Success!',
          text: 'Ticket created successfully!',
          icon: 'success',
          confirmButtonColor: '#14b8a6'
        });
        navigate('/tickets');
      } else {
        toast.error(res.data.message);
      }
    } catch (err: any) {
      console.error(err);
      toast.error(err.response?.data?.message || 'Failed to create ticket');
    } finally {
      setLoading(false);
    }
  };

  const availableSubCategories = formData.category ? CATEGORIES[formData.category as keyof typeof CATEGORIES] : [];
  // Show product form ONLY when category is Add Product or navigated via banner
  const isProductRequestForm = formData.category === 'Add Product' || location.state?.productRequest === true;

  return (
    <div className="fade-in" style={{ width: '100%', paddingBottom: '40px' }}>
      <style>{`
        @media (max-width: 768px) {
          .responsive-grid-2 { grid-template-columns: 1fr !important; }
          .responsive-grid-3 { grid-template-columns: 1fr !important; }
        }
      `}</style>
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
        <button onClick={() => navigate('/tickets/category')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '8px', display: 'flex', alignItems: 'center', color: '#64748b' }}>
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: '800', color: '#0f172a' }}>
            {isProductRequestForm ? 'New Product Request' : 'Create Support Ticket'}
          </h1>
          <p style={{ color: '#64748b', fontSize: '14px', marginTop: '4px' }}>
            {isProductRequestForm ? 'Request a new product to be added to the catalog' : 'Submit a new request for assistance'}
          </p>
        </div>
      </div>

      <div style={{ background: 'white', borderRadius: '16px', padding: '32px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>

          <div className="responsive-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#334155', marginBottom: '8px' }}>Ticket Category <span style={{ color: '#ef4444' }}>*</span></label>
              <div style={{
                display: 'flex', alignItems: 'center', gap: '12px',
                width: '100%', padding: '8px 16px', borderRadius: '10px',
                border: '1px solid #e2e8f0', background: '#f8fafc', boxSizing: 'border-box'
              }}>
                <div style={{
                  width: '30px', height: '30px', borderRadius: '8px',
                  background: CATEGORY_META[formData.category]?.bg || '#f1f5f9',
                  color: CATEGORY_META[formData.category]?.color || '#64748b',
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                  {CATEGORY_META[formData.category]?.icon || <HelpCircle size={20} />}
                </div>
                <span style={{ fontSize: '15px', fontWeight: '600', color: '#1e293b' }}>
                  {formData.category}
                </span>
              </div>
            </div>

            <div ref={priorityRef} style={{ position: 'relative' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#334155', marginBottom: '8px' }}>Priority <span style={{ color: '#ef4444' }}>*</span></label>
              <div
                onClick={() => setPriorityOpen(o => !o)}
                style={{
                  width: '100%', padding: '12px 16px', borderRadius: '10px',
                  border: '1px solid #e2e8f0', fontSize: '14px', background: 'white',
                  cursor: 'pointer', display: 'flex', alignItems: 'center',
                  justifyContent: 'space-between', userSelect: 'none', boxSizing: 'border-box'
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#0f172a' }}>
                  <span style={{
                    width: '10px', height: '10px', borderRadius: '50%', flexShrink: 0, display: 'inline-block',
                    background: PRIORITY_OPTIONS.find(p => p.label === formData.priority)?.color || '#94a3b8'
                  }} />
                  {formData.priority}
                </span>
                <span style={{ fontSize: '12px', color: '#94a3b8', transform: priorityOpen ? 'rotate(180deg)' : 'rotate(0)', transition: '0.2s', lineHeight: 1 }}>▼</span>
              </div>

              {priorityOpen && (
                <div style={{
                  position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0,
                  background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0',
                  boxShadow: '0 8px 24px -4px rgba(0,0,0,0.12)', zIndex: 100, overflow: 'hidden'
                }}>
                  {PRIORITY_OPTIONS.map(opt => {
                    const isSelected = formData.priority === opt.label;
                    return (
                      <div
                        key={opt.label}
                        onClick={() => { setFormData({ ...formData, priority: opt.label }); setPriorityOpen(false); }}
                        style={{
                          padding: '11px 16px', display: 'flex', alignItems: 'center',
                          gap: '12px', cursor: 'pointer', fontSize: '14px',
                          color: isSelected ? '#14b8a6' : '#334155', fontWeight: isSelected ? '600' : '400',
                          background: isSelected ? '#f0fdfa' : 'white', transition: 'background 0.12s'
                        }}
                        onMouseEnter={e => { if (!isSelected) (e.currentTarget as HTMLDivElement).style.background = '#f8fafc'; }}
                        onMouseLeave={e => { if (!isSelected) (e.currentTarget as HTMLDivElement).style.background = 'white'; }}
                      >
                        <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: opt.color, flexShrink: 0, display: 'inline-block' }} />
                        <span style={{ flex: 1 }}>{opt.label}</span>
                        {isSelected && <span style={{ color: '#14b8a6', fontSize: '16px' }}>✓</span>}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            {isProductRequestForm && (
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#334155', marginBottom: '8px' }}>Subject <span style={{ color: '#ef4444' }}>*</span></label>
                <input
                  required
                  type="text"
                  value={formData.subject}
                  maxLength={100}
                  onChange={e => {
                    setFormData({ ...formData, subject: e.target.value.trimStart() });
                    if (errors.subject) setErrors({ ...errors, subject: '' });
                  }}
                  style={{ width: '100%', padding: '12px 16px', borderRadius: '10px', border: `1px solid ${errors.subject ? '#ef4444' : '#e2e8f0'}`, outline: 'none', fontSize: '14px' }}
                  placeholder="Brief summary of your product request"
                />
                {errors.subject && <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '6px', fontWeight: '500' }}>{errors.subject}</div>}
              </div>
            )}
          </div>

          {isProductRequestForm && (
            <>
              {/* ── PRODUCT SELECTION CARD ── */}
              {/* <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: '20px' }}>
                <h2 style={{ fontSize: '16px', fontWeight: '700', color: '#1e293b', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ color: '#14b8a6' }}>❖</span> Select Your Product
                  <span style={{ fontSize: '12px', fontWeight: '400', color: '#94a3b8', marginLeft: '4px' }}>(optional — click to pre-fill details)</span>
                </h2>

                <div style={{ position: 'relative', marginBottom: '16px' }}>
                  <Search size={16} style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                  <input
                    type="text"
                    value={productSearch}
                    onChange={e => setProductSearch(e.target.value)}
                    placeholder="Search your products by name, brand or category..."
                    style={{ width: '100%', padding: '12px 16px 12px 40px', borderRadius: '10px', border: '1px solid #e2e8f0', outline: 'none', fontSize: '14px', boxSizing: 'border-box' }}
                  />
                </div>

                {loadingProducts ? (
                  <div style={{ textAlign: 'center', padding: '24px', color: '#94a3b8', fontSize: '14px' }}>Loading products...</div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px' }}>
                    {filteredProducts.map((product: any) => {
                      const isSelected = selectedProduct?._id === product._id;
                      const name = product.name || product.productName || 'Unnamed Product';
                      const brand = product.brand || product.brandName || '';
                      const category = product.category || product.productCategory || '';
                      const imageUrl = product.images?.[0] || product.productImages?.[0] || null;
                      return (
                        <div
                          key={product._id}
                          onClick={() => handleSelectProduct(product)}
                          style={{
                            border: isSelected ? '2px solid #14b8a6' : '1px solid #e2e8f0',
                            borderRadius: '12px',
                            padding: '14px',
                            cursor: 'pointer',
                            background: isSelected ? '#f0fdfa' : 'white',
                            transition: 'all 0.15s ease',
                            position: 'relative',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '8px',
                          }}
                          onMouseEnter={e => { if (!isSelected) (e.currentTarget as HTMLDivElement).style.borderColor = '#14b8a6'; }}
                          onMouseLeave={e => { if (!isSelected) (e.currentTarget as HTMLDivElement).style.borderColor = '#e2e8f0'; }}
                        >
                          {isSelected && (
                            <div style={{ position: 'absolute', top: '8px', right: '8px', color: '#14b8a6' }}>
                              <CheckCircle2 size={18} fill="#14b8a6" color="white" />
                            </div>
                          )}
                          <div style={{ width: '100%', height: '80px', borderRadius: '8px', background: '#f8fafc', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {imageUrl ? (
                              <img src={imageUrl} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : (
                              <Package size={28} color="#cbd5e1" />
                            )}
                          </div>
                          <div>
                            <div style={{ fontSize: '13px', fontWeight: '700', color: '#1e293b', lineHeight: '1.3', marginBottom: '2px' }}>{name}</div>
                            {brand && <div style={{ fontSize: '11px', color: '#64748b' }}>{brand}</div>}
                            {category && <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px', background: '#f1f5f9', borderRadius: '4px', padding: '1px 6px', display: 'inline-block' }}>{category}</div>}
                          </div>
                        </div>
                      );
                    })}
                    {filteredProducts.length === 0 && !loadingProducts && (
                      <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: '24px', color: '#94a3b8', fontSize: '14px' }}>
                        No products found. Fill in the details manually below.
                      </div>
                    )}
                  </div>
                )}
              </div> */}

              <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: '20px' }}>
                <h2 style={{ fontSize: '16px', fontWeight: '700', color: '#1e293b', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ color: '#8b5cf6' }}>❖</span> Basic Information
                </h2>
                <div className="responsive-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#334155', marginBottom: '8px' }}>Product Name <span style={{ color: '#ef4444' }}>*</span></label>
                    <input
                      type="text"
                      value={formData.productName}
                      onChange={e => {
                        setFormData({ ...formData, productName: e.target.value.trimStart() });
                        if (errors.productName) setErrors({ ...errors, productName: '' });
                      }}
                      style={{ width: '100%', padding: '12px 16px', borderRadius: '10px', border: `1px solid ${errors.productName ? '#ef4444' : '#e2e8f0'}`, outline: 'none', fontSize: '14px' }}
                      placeholder="e.g. 3M Filtek Z350 XT Composite Resin"
                    />
                    {errors.productName && <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '6px', fontWeight: '500' }}>{errors.productName}</div>}
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#334155', marginBottom: '8px' }}>Sub Title</label>
                    <input
                      type="text"
                      maxLength={200}
                      value={formData.subTitle}
                      onChange={e => setFormData({ ...formData, subTitle: e.target.value.trimStart() })}
                      style={{ width: '100%', padding: '12px 16px', borderRadius: '10px', border: '1px solid #e2e8f0', outline: 'none', fontSize: '14px' }}
                      placeholder="e.g. Universal Nanocomposite for Aesthetic Restorations"
                    />
                  </div>
                </div>

                <div className="responsive-grid-3" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                  <div ref={productCategoryRef} style={{ position: 'relative', zIndex: 10 }}>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#334155', marginBottom: '8px' }}>Product Category <span style={{ color: '#ef4444' }}>*</span></label>
                    <div
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); setProductCategoryOpen(o => !o); }}
                      style={{ width: '100%', padding: '12px 16px', borderRadius: '10px', border: `1px solid ${errors.productCategory ? '#ef4444' : '#e2e8f0'}`, fontSize: '14px', background: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', userSelect: 'none', boxSizing: 'border-box' }}
                    >
                      <span style={{ display: 'flex', alignItems: 'center', gap: '10px', color: formData.productCategory ? '#0f172a' : '#94a3b8' }}>
                        {formData.productCategory || 'Select category'}
                      </span>
                      <span style={{ fontSize: '12px', color: '#94a3b8', transform: productCategoryOpen ? 'rotate(180deg)' : 'rotate(0)', transition: '0.2s', lineHeight: 1 }}>▼</span>
                    </div>
                    {errors.productCategory && <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '6px', fontWeight: '500' }}>{errors.productCategory}</div>}
                    {productCategoryOpen && (
                      <div style={{ position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0, background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 8px 24px -4px rgba(0,0,0,0.12)', zIndex: 100, overflow: 'hidden', maxHeight: '200px', overflowY: 'auto' }}>
                        {dbCategories.map(catObj => {
                          const c = catObj.category;
                          const isSelected = formData.productCategory === c;
                          return (
                            <div
                              key={c}
                              onClick={() => {
                                setFormData({ ...formData, productCategory: c });
                                if (errors.productCategory) setErrors({ ...errors, productCategory: '' });
                                setProductCategoryOpen(false);
                              }}
                              style={{ padding: '11px 16px', display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', fontSize: '14px', color: isSelected ? '#14b8a6' : '#334155', fontWeight: isSelected ? '600' : '400', background: isSelected ? '#f0fdfa' : 'white', transition: 'background 0.12s' }}
                              onMouseEnter={e => { if (!isSelected) (e.currentTarget as HTMLDivElement).style.background = '#f8fafc'; }}
                              onMouseLeave={e => { if (!isSelected) (e.currentTarget as HTMLDivElement).style.background = 'white'; }}
                            >
                              <span style={{ flex: 1 }}>{c}</span>
                              {isSelected && <span style={{ color: '#14b8a6', fontSize: '16px' }}>✓</span>}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#334155', marginBottom: '8px' }}>Product Sub Category <span style={{ color: '#ef4444' }}>*</span></label>
                    <input
                      type="text"
                      value={formData.productSubCategory}
                      maxLength={70}
                      onChange={e => {
                        setFormData({ ...formData, productSubCategory: e.target.value.trimStart() });
                        if (errors.productSubCategory) setErrors({ ...errors, productSubCategory: '' });
                      }}
                      style={{ width: '100%', padding: '12px 16px', borderRadius: '10px', border: `1px solid ${errors.productSubCategory ? '#ef4444' : '#e2e8f0'}`, outline: 'none', fontSize: '14px' }}
                      placeholder="e.g. 2nd Year BDS"
                    />
                    {errors.productSubCategory && <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '6px', fontWeight: '500' }}>{errors.productSubCategory}</div>}
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#334155', marginBottom: '8px' }}>Brand</label>
                    <input
                      type="text"
                      value={formData.brandName}
                      maxLength={70}
                      onChange={e => setFormData({ ...formData, brandName: e.target.value.trimStart() })}
                      style={{ width: '100%', padding: '12px 16px', borderRadius: '10px', border: '1px solid #e2e8f0', outline: 'none', fontSize: '14px' }}
                      placeholder="e.g. 3M"
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#334155', marginBottom: '8px' }}>Tags</label>
                    <input
                      type="text"
                      value={formData.tags}
                      maxLength={200}
                      onChange={e => setFormData({ ...formData, tags: e.target.value.trimStart() })}
                      style={{ width: '100%', padding: '12px 16px', borderRadius: '10px', border: '1px solid #e2e8f0', outline: 'none', fontSize: '14px' }}
                      placeholder="comma separated tags"
                    />
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#334155', marginBottom: '8px' }}>Description</label>
                  <textarea
                    rows={4}
                    maxLength={500}
                    value={formData.description}
                    onChange={e => setFormData({ ...formData, description: e.target.value.trimStart() })}
                    style={{ width: '100%', padding: '12px 16px', borderRadius: '10px', border: '1px solid #e2e8f0', outline: 'none', fontSize: '14px', resize: 'vertical' }}
                    placeholder="Detailed product description..."
                  />
                </div>
              </div>

              <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '20px' }}>
                <h2 style={{ fontSize: '16px', fontWeight: '700', color: '#1e293b', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Package size={20} color="#10b981" />
                  Product Status
                </h2>
                <div className="responsive-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '24px' }}>
                  <div ref={productStatusRef} style={{ position: 'relative', zIndex: 10 }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#94a3b8', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Product Status</label>
                    <div
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); setProductStatusOpen(o => !o); }}
                      style={{ width: '100%', padding: '12px 16px', borderRadius: '10px', border: '1px solid #e2e8f0', fontSize: '14px', background: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', userSelect: 'none', boxSizing: 'border-box' }}
                    >
                      <span style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#0f172a' }}>
                        <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: PRODUCT_STATUS_OPTIONS.find(s => s.label === formData.productStatus)?.color || '#94a3b8', flexShrink: 0, display: 'inline-block' }} />
                        {formData.productStatus}
                      </span>
                      <span style={{ fontSize: '12px', color: '#94a3b8', transform: productStatusOpen ? 'rotate(180deg)' : 'rotate(0)', transition: '0.2s', lineHeight: 1 }}>▼</span>
                    </div>
                    {productStatusOpen && (
                      <div style={{ position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0, background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 8px 24px -4px rgba(0,0,0,0.12)', zIndex: 100, overflow: 'hidden' }}>
                        {PRODUCT_STATUS_OPTIONS.map(opt => {
                          const isSelected = formData.productStatus === opt.label;
                          return (
                            <div
                              key={opt.label}
                              onClick={() => { setFormData({ ...formData, productStatus: opt.label }); setProductStatusOpen(false); }}
                              style={{ padding: '11px 16px', display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', fontSize: '14px', color: isSelected ? '#14b8a6' : '#334155', fontWeight: isSelected ? '600' : '400', background: isSelected ? '#f0fdfa' : 'white', transition: 'background 0.12s' }}
                              onMouseEnter={e => { if (!isSelected) (e.currentTarget as HTMLDivElement).style.background = '#f8fafc'; }}
                              onMouseLeave={e => { if (!isSelected) (e.currentTarget as HTMLDivElement).style.background = 'white'; }}
                            >
                              <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: opt.color, flexShrink: 0, display: 'inline-block' }} />
                              <span style={{ flex: 1 }}>{opt.label}</span>
                              {isSelected && <span style={{ color: '#14b8a6', fontSize: '16px' }}>✓</span>}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '20px' }}>
                <h2 style={{ fontSize: '16px', fontWeight: '700', color: '#1e293b', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Sliders size={20} color="#64748b" />
                  Specifications
                </h2>
                <div className="responsive-grid-3" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Weight (g)</label>
                    <input
                      type="text"
                      value={formData.weight}
                      onChange={e => setFormData({ ...formData, weight: e.target.value.replace(/[^0-9.]/g, '') })}
                      style={{ width: '100%', padding: '12px 16px', borderRadius: '10px', border: '1px solid #e2e8f0', outline: 'none', fontSize: '14px' }}
                      placeholder="e.g. 4"
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Height (cm)</label>
                    <input
                      type="text"
                      value={formData.height}
                      onChange={e => setFormData({ ...formData, height: e.target.value.replace(/[^0-9.]/g, '') })}
                      style={{ width: '100%', padding: '12px 16px', borderRadius: '10px', border: '1px solid #e2e8f0', outline: 'none', fontSize: '14px' }}
                      placeholder="e.g. 10"
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Width (cm)</label>
                    <input
                      type="text"
                      value={formData.width}
                      onChange={e => setFormData({ ...formData, width: e.target.value.replace(/[^0-9.]/g, '') })}
                      style={{ width: '100%', padding: '12px 16px', borderRadius: '10px', border: '1px solid #e2e8f0', outline: 'none', fontSize: '14px' }}
                      placeholder="e.g. 5"
                    />
                  </div>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Key Specifications</label>
                  <textarea
                    rows={4}
                    maxLength={600}
                    value={formData.keySpecifications}
                    onChange={e => setFormData({ ...formData, keySpecifications: e.target.value.trimStart() })}
                    style={{ width: '100%', padding: '12px 16px', borderRadius: '10px', border: '1px solid #e2e8f0', outline: 'none', fontSize: '14px', resize: 'vertical' }}
                    placeholder="Enter key specifications here..."
                  />
                </div>
                <div style={{ marginTop: '16px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Product Images (Max 5)</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                    {images.map((img, index) => (
                      <div key={index} style={{ position: 'relative', width: '80px', height: '80px', borderRadius: '8px', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                        <img src={URL.createObjectURL(img)} alt={`upload-${index}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        <button
                          type="button"
                          onClick={(e) => { e.preventDefault(); removeImage(index); }}
                          style={{ position: 'absolute', top: '4px', right: '4px', background: 'rgba(239, 68, 68, 0.9)', color: 'white', border: 'none', borderRadius: '50%', width: '20px', height: '20px', padding: '0', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                    {images.length < 5 && (
                      <label style={{ width: '80px', height: '80px', borderRadius: '8px', border: '2px dashed #cbd5e1', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', background: '#f8fafc', color: '#64748b', transition: 'all 0.2s' }}>
                        <Upload size={20} style={{ marginBottom: '4px' }} />
                        <span style={{ fontSize: '10px', fontWeight: '600' }}>Upload</span>
                        <input
                          type="file"
                          multiple
                          accept=".jpg,.jpeg,.png"
                          onChange={handleImageChange}
                          style={{ display: 'none' }}
                        />
                      </label>
                    )}
                  </div>
                  <p style={{ fontSize: '12px', color: '#94a3b8', marginTop: '8px' }}>Accepted formats: JPG, JPEG, PNG. Max 5 images.</p>
                </div>
              </div>
            </>
          )}

          {!isProductRequestForm && formData.category && (
            <>
              <div className="responsive-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#334155', marginBottom: '8px' }}>
                    Sub Category <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input
                    type="text"
                    list="subcategory-suggestions"
                    value={formData.subCategory}
                    maxLength={100}
                    onChange={e => {
                      setFormData({ ...formData, subCategory: e.target.value.trimStart() });
                      if (errors.subCategory) setErrors({ ...errors, subCategory: '' });
                    }}
                    style={{
                      width: '100%', padding: '12px 16px', borderRadius: '10px',
                      border: `1px solid ${errors.subCategory ? '#ef4444' : '#e2e8f0'}`,
                      outline: 'none', fontSize: '14px', boxSizing: 'border-box'
                    }}
                    placeholder="Enter sub category"
                  />
                  {availableSubCategories && availableSubCategories.length > 0 && (
                    <datalist id="subcategory-suggestions">
                      {availableSubCategories.map((sub: string) => (
                        <option key={sub} value={sub} />
                      ))}
                    </datalist>
                  )}
                  {errors.subCategory && <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '6px', fontWeight: '500' }}>{errors.subCategory}</div>}
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#334155', marginBottom: '8px' }}>
                    Subject <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input
                    required
                    type="text"
                    value={formData.subject}
                    maxLength={100}
                    onChange={e => {
                      setFormData({ ...formData, subject: e.target.value.trimStart() });
                      if (errors.subject) setErrors({ ...errors, subject: '' });
                    }}
                    style={{
                      width: '100%', padding: '12px 16px', borderRadius: '10px',
                      border: `1px solid ${errors.subject ? '#ef4444' : '#e2e8f0'}`,
                      outline: 'none', fontSize: '14px', boxSizing: 'border-box'
                    }}
                    placeholder="Brief summary of your issue"
                  />
                  {errors.subject && <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '6px', fontWeight: '500' }}>{errors.subject}</div>}
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#334155', marginBottom: '8px' }}>
                  Description <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <textarea
                  required
                  rows={6}
                  value={formData.description}
                  onChange={e => {
                    setFormData({ ...formData, description: e.target.value.trimStart() });
                    if (errors.description) setErrors({ ...errors, description: '' });
                  }}
                  style={{
                    width: '100%', padding: '12px 16px', borderRadius: '10px',
                    border: `1px solid ${errors.description ? '#ef4444' : '#e2e8f0'}`,
                    outline: 'none', fontSize: '14px', resize: 'vertical', boxSizing: 'border-box'
                  }}
                  placeholder="Please provide detailed information about your issue..."
                />
                {errors.description && <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '6px', fontWeight: '500' }}>{errors.description}</div>}
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#334155', marginBottom: '8px' }}>
                  Attachments (Max 5)
                </label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                  {images.map((img, index) => (
                    <div key={index} style={{ position: 'relative', width: '80px', height: '80px', borderRadius: '8px', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                      <img src={URL.createObjectURL(img)} alt={`upload-${index}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      <button
                        type="button"
                        onClick={(e) => { e.preventDefault(); removeImage(index); }}
                        style={{ position: 'absolute', top: '4px', right: '4px', background: 'rgba(239, 68, 68, 0.9)', color: 'white', border: 'none', borderRadius: '50%', width: '20px', height: '20px', padding: '0', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                  {images.length < 5 && (
                    <label style={{ width: '80px', height: '80px', borderRadius: '8px', border: '2px dashed #cbd5e1', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', background: '#f8fafc', color: '#64748b', transition: 'all 0.2s' }}>
                      <Upload size={20} style={{ marginBottom: '4px' }} />
                      <span style={{ fontSize: '10px', fontWeight: '600' }}>Upload</span>
                      <input
                        type="file"
                        multiple
                        accept=".jpg,.jpeg,.png"
                        onChange={handleImageChange}
                        style={{ display: 'none' }}
                      />
                    </label>
                  )}
                </div>
                <p style={{ fontSize: '12px', color: '#94a3b8', marginTop: '8px' }}>Accepted formats: JPG, JPEG, PNG. Max 5 images.</p>
              </div>
            </>
          )}




          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '8px', paddingTop: '24px', borderTop: '1px solid #f1f5f9' }}>
            <button type="button" onClick={() => navigate('/tickets/category')} style={{ padding: '12px 24px', borderRadius: '10px', border: '1px solid #e2e8f0', background: 'white', color: '#475569', fontWeight: '600', cursor: 'pointer' }}>
              Cancel
            </button>
            <button type="submit" disabled={loading} style={{ padding: '12px 32px', borderRadius: '10px', border: 'none', background: '#14b8a6', color: 'white', fontWeight: '600', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1 }}>
              {loading ? 'Submitting...' : 'Submit Ticket'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
